/**
 * context.js — every React context of Daily Ops, provided once by the DailyOps root.
 *
 *   ToastCtx     notify(message, undo?) — the undo-able toast
 *   MobileCtx    true on touch devices (iPad + phone) → tap / swipe / bottom-sheet rows
 *   OpenListCtx  the open list's gate (see below)
 *   ListsCtx     the lists on the board (built-ins + made in the app) and item routing
 */
import { createContext, useContext } from 'react';
import { buildListsState } from './logic/lists';

export const ToastCtx = createContext(() => {});
export const useToast = () => useContext(ToastCtx);

export const MobileCtx = createContext(false);
export const useMobile = () => useContext(MobileCtx);

/**
 * The open list's gate.
 *   withList(run, label) — call before ADDING to the board. Runs `run(listDay)` right
 *     away, unless a new list cycle has begun on a list nobody has added to yet — then
 *     it first asks "move the open list to History, or keep adding to it?".
 *   openMove() — the manual "📦 Move list to History" dialog.
 *   board — boardState() of the open list (listDay, firstDay, stale, …).
 * The default (no provider, e.g. isolated tests) just runs immediately.
 */
export const OpenListCtx = createContext({
  withList: run => run(undefined, { archived: new Set() }),
  openMove: () => {},
  board: null,
});
export const useOpenList = () => useContext(OpenListCtx);

/** Built-in lists only until the app-made list docs load (see logic/lists.js). */
export const ListsCtx = createContext(buildListsState());
export const useLists = () => useContext(ListsCtx);
